import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { generateSecureAlphanumeric, getSecureRandomInt } from '../../lib/secureRandom'
import { RefreshCw, Check, X, ArrowRight } from 'lucide-react'

interface ChooseLidProps {
  onLidSelected: (lid: string) => void
}

/**
 * ChooseLid - Minimal identifier selection
 *
 * Pure black/white aesthetic with subtle interactions.
 */
export default function ChooseLid({ onLidSelected }: ChooseLidProps) {
  const [suggestedLids, setSuggestedLids] = useState<string[]>([])
  const [customLid, setCustomLid] = useState('')
  const [isChecking, setIsChecking] = useState(false)
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const generateSuggestions = () => {
    const suggestions = Array.from({ length: 3 }, () => {
      const prefix = 'LUKH'
      const suffix = generateSecureAlphanumeric(8)
      return `ΛiD#${prefix}-${suffix}`
    })
    setSuggestedLids(suggestions)
  }

  useEffect(() => {
    generateSuggestions()
  }, [])

  const handleRefresh = () => {
    setIsRefreshing(true)
    setTimeout(() => {
      generateSuggestions()
      setIsRefreshing(false)
    }, 300)
  }

  const checkAvailability = async (lid: string) => {
    setIsChecking(true)
    await new Promise((resolve) => setTimeout(resolve, 500))
    const available = getSecureRandomInt(10) > 3
    setIsAvailable(available)
    setIsChecking(false)
  }

  const handleCustomLidChange = (value: string) => {
    if (!value.startsWith('ΛiD#')) {
      value = 'ΛiD#' + value.replace(/^ΛiD#/, '')
    }

    let remainder = value.slice(4).toUpperCase()
    const parts = remainder.split('-')
    let prefix = parts[0] || ''
    let suffix = parts[1] || ''

    prefix = prefix.replace(/[^A-Z]/g, '').slice(0, 4)
    suffix = suffix.replace(/[^A-Z0-9]/g, '').slice(0, 8)

    let cleanValue = 'ΛiD#' + prefix
    if (suffix || prefix.length >= 2) {
      cleanValue += '-' + suffix
    }

    setCustomLid(cleanValue)
    setIsAvailable(null)

    if (prefix.length >= 2 && suffix.length === 8) {
      checkAvailability(cleanValue)
    }
  }

  const handleSelectLid = (lid: string) => {
    onLidSelected(lid)
  }

  return (
    <div className="space-y-8">
      {/* Suggested identifiers */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-white/30 uppercase tracking-widest">
            Available
          </p>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 text-xs text-white/30 hover:text-white/60
                     disabled:opacity-30 transition-colors duration-300"
          >
            <RefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        <div className="space-y-2">
          {suggestedLids.map((lid, index) => (
            <motion.button
              key={lid}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleSelectLid(lid)}
              className="w-full flex items-center justify-between p-4
                       border border-white/10 hover:border-white/20
                       transition-all duration-300 group"
            >
              <div className="flex items-center gap-4">
                <span className="text-white/20 text-lg font-extralight">Λ</span>
                <span className="font-mono text-white/80 tracking-wide">
                  {lid}
                </span>
              </div>
              <ArrowRight className="w-4 h-4 text-white/10 group-hover:text-white/40
                                   group-hover:translate-x-1 transition-all duration-300" />
            </motion.button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-white/10" />
        <span className="text-[10px] text-white/20 uppercase tracking-widest">
          Or custom
        </span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      {/* Custom input */}
      <div className="space-y-3">
        <div className="relative">
          <input
            id="custom-lid"
            type="text"
            placeholder="ΛiD#XXXX-XXXXXXXX"
            value={customLid}
            onChange={(e) => handleCustomLidChange(e.target.value)}
            className="w-full px-0 py-4
                     bg-transparent border-0 border-b border-white/10
                     text-white placeholder-white/20 font-mono
                     focus:outline-none focus:border-white/30
                     transition-colors duration-300"
            maxLength={20}
          />

          {/* Status indicator */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            {isChecking && (
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                className="w-4 h-4 border border-white/20 border-t-white/60 rounded-full"
              />
            )}
            {isAvailable === true && !isChecking && (
              <Check className="w-4 h-4 text-white/60" />
            )}
            {isAvailable === false && !isChecking && (
              <X className="w-4 h-4 text-white/40" />
            )}
          </div>
        </div>

        <p className="text-xs text-white/20 font-light">
          Format: 2-4 letter prefix, 8 character suffix
        </p>

        {isAvailable === true && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs text-white/50"
          >
            Available
          </motion.p>
        )}
        {isAvailable === false && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs text-white/30"
          >
            Already taken
          </motion.p>
        )}
      </div>

      {/* Continue button for custom */}
      {isAvailable === true && customLid.match(/^ΛiD#[A-Z]{2,4}-[A-Z0-9]{8}$/) && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => handleSelectLid(customLid)}
          className="w-full py-4
                   bg-white text-black text-sm font-medium tracking-wide
                   hover:bg-white/90 active:bg-white/80
                   transition-all duration-200
                   flex items-center justify-center gap-3"
        >
          <span>Use {customLid}</span>
          <ArrowRight className="w-4 h-4" />
        </motion.button>
      )}
    </div>
  )
}
